import { useMemo, useState, type RefObject } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import type { Faq } from '@vibesites/schema';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
  useCollectionBinding,
  useEditMode,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const UNCATEGORIZED_LABEL = 'General';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Contact CTA Link',
  fields: {
    url:     { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label:   { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'null' && s !== 'undefined';
}

// ============================================================================
// Accordion item
// ============================================================================

interface FAQItemProps {
  question: string;
  answerHtml: string;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ question, answerHtml, index, isOpen, onToggle }: FAQItemProps) {
  const panelId = `faq-panel-${index}`;
  const buttonId = `faq-button-${index}`;

  return (
    <div data-reveal className={`reveal-fade-up faq-item${isOpen ? ' faq-item--open' : ''}`}>
      <button
        id={buttonId}
        className="faq-question"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        type="button"
      >
        <span className="faq-question-text">{question}</span>
        <span className="faq-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className="faq-answer-wrap"
        hidden={!isOpen}
      >
        <div
          className="faq-answer"
          dangerouslySetInnerHTML={{ __html: answerHtml }}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function FAQList() {
  useRevealStyles();
  const listRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 80 });

  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const ctaLink = useSlotGroup('faqlist.cta_link', ctaLinkDef);
  const faqs = useCollectionBinding<Faq>('faqlist.items', {
    collection: 'faqs',
  });

  // Track which item is open (single-open accordion, indexed by global position)
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  // Group by category, preserving first-seen order
  const grouped = useMemo(() => {
    const order: string[] = [];
    const byCategory: Record<string, Faq[]> = {};
    faqs.forEach((faq) => {
      const cat = hasValue(faq.category) ? faq.category!.trim() : UNCATEGORIZED_LABEL;
      if (!byCategory[cat]) {
        byCategory[cat] = [];
        order.push(cat);
      }
      byCategory[cat].push(faq);
    });
    return order.map((cat) => ({ category: cat, items: byCategory[cat] }));
  }, [faqs]);

  const hasFaqs = faqs.length > 0;

  // Track a running global index across categories so accordion state
  // is unambiguous even when the same question appears in two categories.
  let runningIndex = -1;

  return (
    <section className="reveal-fade-up py-12 md:py-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding bindingPath="faqlist.items" label="FAQ Items">
          {hasFaqs ? (
            <div ref={listRef as RefObject<HTMLDivElement>} className="space-y-10">
              {grouped.map((group) => (
                <div key={group.category} className="faq-group">
                  {grouped.length > 1 && (
                    <h3 className="faq-group-heading">{group.category}</h3>
                  )}
                  <div className="faq-list">
                    {group.items.map((faq) => {
                      runningIndex += 1;
                      const idx = runningIndex;
                      const question = hasValue(faq.question) ? faq.question! : '';
                      const answerHtml = hasValue(faq.answer) ? faq.answer! : '';
                      if (!question) return null;
                      return (
                        <FAQItem
                          key={`${group.category}-${idx}`}
                          index={idx}
                          question={question}
                          answerHtml={answerHtml}
                          isOpen={openIndex === idx}
                          onToggle={() =>
                            setOpenIndex((prev) => (prev === idx ? null : idx))
                          }
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
              <p className="font-body text-base">
                {isEditMode
                  ? 'Add FAQs in the Multihub property profile for them to appear here.'
                  : 'FAQs will be displayed here.'}
              </p>
            </div>
          )}
        </EditableCollectionBinding>

        <div className="mt-12 text-center">
          <RichTextContent
            path="faqlist.cta_prompt"
            label="CTA Prompt"
            as="p"
            className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] mb-4"
          />
          <EditableSlotGroup groupPath="faqlist.cta_link" definition={ctaLinkDef}>
            <a
              href={ctaLink.url || '/contact-us'}
              target={ctaLink._target || '_self'}
              className="faq-cta"
            >
              {ctaLink.label || 'Contact Us'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .faq-group-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.15rem;
          font-weight: 600;
          color: var(--vibe-color-primary);
          text-transform: uppercase;
          letter-spacing: 0.12em;
          margin: 0 0 1rem 0.25rem;
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          border-top: 1px solid var(--vibe-color-border);
        }

        .faq-item {
          border-bottom: 1px solid var(--vibe-color-border);
        }

        .faq-question {
          width: 100%;
          background: transparent;
          border: none;
          padding: 1.1rem 0.25rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          text-align: left;
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 500;
          color: var(--vibe-color-text);
          cursor: pointer;
          transition: color 0.2s ease;
        }
        .faq-question:hover {
          color: var(--vibe-color-primary);
        }
        .faq-question-text {
          flex: 1;
          line-height: 1.4;
        }
        .faq-icon {
          flex-shrink: 0;
          width: 22px;
          height: 22px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: var(--vibe-color-primary);
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .faq-icon svg {
          width: 100%;
          height: 100%;
        }
        .faq-item--open .faq-icon {
          transform: rotate(180deg);
        }

        .faq-answer-wrap {
          overflow: hidden;
        }
        .faq-answer {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
          padding: 0 0.25rem 1.25rem;
        }
        .faq-answer p { margin: 0 0 0.75rem; }
        .faq-answer p:last-child { margin-bottom: 0; }
        .faq-answer ul,
        .faq-answer ol {
          padding-left: 1.25rem;
          margin: 0 0 0.75rem;
        }
        .faq-answer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
          text-underline-offset: 2px;
        }

        /* CTA — AMC pill shape, matching PetPolicy */
        .faq-cta {
          display: inline-flex;
          align-items: center;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--vibe-color-text-on-primary);
          background: var(--vibe-color-primary);
          padding: 0.875rem 2rem;
          border: none;
          border-radius: 9999px;
          text-decoration: none;
          transition: all 0.3s ease;
        }
        .faq-cta:hover {
          transform: translateY(-1px);
          opacity: 0.92;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQList',
  displayName: 'FAQ List',
  tokens: {
    'faqlist.cta_prompt': {
      label: 'CTA Prompt',
      type: 'richtext',
      defaultValue: '<p>Still have questions?</p>',
    },
  },
  slotGroups: {
    'faqlist.cta_link': {
      label: 'Contact CTA Link',
      fields: {
        url:     { label: 'URL', type: 'url', defaultValue: '/contact-us' },
        label:   { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQ Items',
      description: 'FAQ entries pulled from the property\'s FAQ collection, grouped by category.',
      collection: 'faqs',
    },
  },
};

(FAQList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQList;
